import {
  PlanItem,
  PositiveNumber,
  ProductName,
  ServiceName,
} from "../interfaces"

import { PlanItemProductName } from "./productKeys"

// 「単体で買い足せる商品」の正本。**ここ 1 箇所だけ**に置く。
//
// 以前は 2 箇所に分かれていて、片方にしかガードがありませんでした（#415）:
//   - `usePurchaseModal.ts` の `buyableItems` … 購入確認モーダルを開いてよいか
//   - `SinglePurchases.tsx` のハードコードした CMS / CLOUD 分岐 … セルを描くか
// `SinglePurchases` の「選択」ボタンは `usePurchaseModal` を通さず
// `setPurchaseConfirmModal` を直接呼んでいたため、**`buyableItems.cloud` を空に
// しても（#414）、`SinglePurchases` 側の TODO ガードを外せば CLOUD で購入確認が
// 開く**状態でした。描画も開閉もこの表から導くことで、経路を 1 本に寄せています。
//
// 案C（2026-09-15 確定）の CLOUD には、**前払いで数量を買い足す商品が 1 つも
// ありません**（auth-api #1183 の `config/plans.yml` の `v4: cloud:`）:
//   - `membership` / `custom_domain` / `site` … 全プラン `price: 0`
//     （membership は `included: ~` で全プラン無制限。シート課金をしない）
//   - `bandwidth` / `build_time` … 有償 4 プランで price > 0 だが**従量課金**で、
//     使った分が自動で請求される。買い足す商品ではないので購入 UI には載せない
//   - `form` … CLOUD には商品自体が存在しない
// したがって cloud は空配列で、`SinglePurchases` は**節ごと描きません**。
//
// CLOUD で何かを単体購入できるようにするときは、`plans.yml` で price > 0 の
// 商品を挙げてからこの表に足すこと。ここに足せば描画も開閉も同時に有効になります。
const BUYABLE_PRODUCTS: Record<ServiceName, readonly PlanItemProductName[]> = {
  cms: ["membership", "form"],
  cloud: [],
}

// そのサービスで単体購入できる商品。空なら単体購入 UI を出さない。
export const buyableProductsFor = (
  serviceName: ServiceName,
): readonly PlanItemProductName[] => BUYABLE_PRODUCTS[serviceName]

// `subscription` を含む `ProductName` でも問い合わせられるようにする
// （クエリパラメータ `?product_name=` は任意の文字列で飛んでくる）。
export const isBuyableProduct = (
  serviceName: ServiceName,
  productName: ProductName,
): boolean =>
  (BUYABLE_PRODUCTS[serviceName] as readonly ProductName[]).includes(
    productName,
  )

// 単体購入のセルに載せてよい「単価」を返す。
//
// **`price: 0` には 2 つの意味があります。** `findProductItem`
// （helpers/planHelpers.ts）は auth-api に product / price が無いとき
// **`price: 0` に倒した PlanItem を返す**ので:
//
//   (a) 単価が引けなかった … `findProductItem` のフォールバック。**売ってはいけない**
//   (b) 正当に無料        … 案C v4 の CLOUD の membership / custom_domain / site
//
// **区別は上の BUYABLE_PRODUCTS が持ちます。** (b) は「前払いで数量を買い足す商品」
// ではないので表に載りません（cloud が空配列なのがその帰結）。したがって
// **表に載っている商品の `price: 0` は (a) しか有り得ず**、セルにしません。
// 「価格 0 の商品は描かない」という単純化ではなく、
// **「買える商品の表に載っていて、かつ単価が引ける」** の後半です。
//
// 表に (b) を足すと、それは「¥0 の購入確認を開く」ことになります。
// `PurchaseConfirmModal` は `!price` で自分を閉じて `notification.error` を出すので
// 理由の出ないエラートーストになります。足す前にそちらの設計を決めてください。
// 使える単価だけを返す（使えなければ undefined）。boolean ではなく値を返すのは、
// 呼び出し側で `price?.` や `!` を書かずに済ませるため。
export const purchasableUnitPrice = (
  item: PlanItem | undefined,
): PositiveNumber | undefined =>
  typeof item?.price === "number" && item.price > 0 ? item.price : undefined
